import { Spinner } from 'components/spinner/Spinner';
import {
    FileLibraryObjectParams,
    useFileLibraryObject,
} from 'custom_hooks/data_hooks/file/useFileLibraryObject';
import * as React from 'react';
import { scalingStyles } from './shared';

type PropsFromImage = 'alt' | 'className' | 'aria-label';
type ImageProps = React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
>;
export type AuthorizedImageProps = Pick<ImageProps, PropsFromImage> & {
    /**
     * Displayed while the image is being loaded.
     */
    pending?: React.ReactNode;
    /**
     * Displayed when there is an error fetching the image or the image source is undefined. Defaults to nothing.
     */
    fallback?: React.ReactNode;

    /**
     * How to scale the image ('object-fit' css property)
     */
    imageScaling?: keyof typeof scalingStyles;
} & FileLibraryObjectParams;

export function AuthorizedImage({
    pending,
    fallback,
    alt,
    className,
    'aria-label': ariaLabel,
    imageScaling,
    ...idProps
}: AuthorizedImageProps) {
    const {
        data: image,
        error,
        isLoading: picturePending,
    } = useFileLibraryObject(idProps);

    if (picturePending) {
        return pending ? (
            <div
                className={className}
                style={imageScaling && scalingStyles[imageScaling]}
            >
                {pending}
            </div>
        ) : (
            <Spinner />
        );
    }

    if (error || !image) {
        return fallback ? (
            <div
                className={className}
                style={imageScaling && scalingStyles[imageScaling]}
            >
                {fallback}
            </div>
        ) : (
            <></>
        );
    }

    return (
        <img
            src={image.blobUrl}
            alt={alt}
            className={className}
            aria-label={ariaLabel}
            style={imageScaling && scalingStyles[imageScaling]}
        />
    );
}
